/**
 * The collections whose page is their slug under a fixed prefix, which is
 * all `pathForEntity` needs. Mirrors the rich-text LinkFeature.enabledCollections
 * set so markdown and Lexical link the same surface.
 */
const SLUG_PATH_COLLECTIONS = [
  'blog-posts',
  'case-studies',
  'manufacturers',
  'pages',
  'policies',
  'product-categories',
  'products',
  'service-categories',
  'services',
  'team-members',
] as const;

type SlugPathCollection = (typeof SLUG_PATH_COLLECTIONS)[number];

/**
 * The collections a markdownRef `link` may point at. This tuple is the single
 * source of truth: the BlogPosts field `relationTo` and href resolution
 * (`buildRefDefinitions`) derive from it. Concerns are linkable from markdown
 * only: their page sits under the tenant-named Concern index, so the href
 * needs where that index lives (`concernPagePath`), not just a slug.
 */
export const MARKDOWN_REF_LINK_COLLECTIONS = [...SLUG_PATH_COLLECTIONS, 'concerns'] as const;

export type MarkdownRefLinkCollection = (typeof MARKDOWN_REF_LINK_COLLECTIONS)[number];

/**
 * Domain-relative public path for a linkable entity. NOT the /tenants/:id
 * internal preview path (that is the force-dynamic noindex tree) — matches the
 * on-site link convention (rich-text.tsx, service-link.tsx) and the search
 * extractor's `url` map.
 */
const PATH_BUILDERS: Record<SlugPathCollection, (slug: string) => string> = {
  'blog-posts': (s) => `/blog/${s}`,
  'case-studies': (s) => `/case-studies/${s}`,
  manufacturers: (s) => `/manufacturers/${s}`,
  pages: (s) => `/${s}`,
  policies: (s) => `/policies/${s}`,
  'product-categories': (s) => `/product-categories/${s}`,
  products: (s) => `/products/${s}`,
  'service-categories': (s) => `/service-categories/${s}`,
  services: (s) => `/services/${s}`,
  'team-members': (s) => `/team-members/${s}`,
};

function isSlugPathCollection(value: string): value is SlugPathCollection {
  return value in PATH_BUILDERS;
}

export function pathForEntity(collection: string, slug: string): string | null {
  if (!slug || !isSlugPathCollection(collection)) return null;
  return PATH_BUILDERS[collection](slug);
}
